'use strict';
import get from 'lodash/get';
import {createEventHandler} from 'recompose';
import {stateGo} from 'redux-ui-router';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/last';
import 'rxjs/add/operator/takeUntil';
import {stateGoBack} from '../../../app/actions';
import {getAssetProjectModel, getProjectModel} from '../../../app/model/reducers/state';
import {
  supportsCamera,
  supportsVideo,
  supportsDownload,
  supportsPickFile, isWebView,
} from '../../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {
  ASSET_FILE_REQUIREMENT_COLLECTION,
  ASSET_FORM_REQUIREMENT_COLLECTION,
  FILE_REQUIREMENT_COLLECTION,
  FORM_REQUIREMENT_COLLECTION,
} from '../../../requirements/domain/config';
import {checkCanAddFile} from '../../../requirements/domain/files/utils';
// eslint-disable-next-line max-len
import {updateTemplateFileRequirementRequestModal} from '../../../requirements/redux/files/updateTemplateFileRequirement';
// eslint-disable-next-line max-len
import {updateTemplateFormRequirementRequestModal} from '../../../requirements/redux/forms/updateTemplateFormRequirement';
import {
  assetFileRequirementStates,
  assetFormRequirementStates,
  projectFileRequirementStates,
  projectFormRequirementStates,
} from '../../../requirements/router-config';
import {TaskType} from '../../enums/TaskType';
import {updateAssetTaskRequestModal} from '../../redux/updateAssetTask';
import {updateProjectTaskRequestModal} from '../../redux/updateProjectTask';
import {updateTemplateTaskRequestModal} from '../../redux/updateTemplateTask';
import {buildTaskPropsByType} from '../../redux/utils';


/**
 * @param {Object} config
 * @param {TaskType} config.taskType
 */
export const TaskViewRequirementsContainer = (config) => (state$, dispatch) => (props$) => {
  const {
    taskType,
    taskListState,
  } = config;
  const isTemplate = taskType === TaskType.TEMPLATE;
  const featureSupport = {
    showCameraButton: !isTemplate && supportsCamera(),
    showVideoButton: !isTemplate && supportsVideo(),
    showUploadButton: !isTemplate && supportsPickFile(),
    showIOSUpButton: !isTemplate && supportsPickFile() && isWebView(),
    enableFileUpload: supportsPickFile(),
    supportsDownload: !isTemplate && supportsDownload(),
  };

  const onGoBack = () => dispatch(stateGoBack({state: taskListState}));
  const {
    showSubmitted,
    showApproved,
    showCancelled,
  } = getShow(taskType);

  const {
    handler: onNavToRequirement,
    stream: navToRequirement$,
  } = createEventHandler();

  const onClickItem = (e, requirement) => {
    e.stopPropagation();
    onNavToRequirement(requirement);
  };

  navToRequirement$
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([requirement, props]) => {
      const requirementId = getId(requirement);
      if (taskType === TaskType.TEMPLATE) {
        const modalActions = getRequirementUpdateModalActions(requirement);

        dispatch(modalActions.showModal({
          model: requirement,
        }));

      } else {
        const detailState = getRequirementDetailsState(requirement);
        dispatch(stateGo(detailState, {requirementId}));
      }
    });

  const {
    stateParams$,
    task$,
    breadcrumbPath$,
  } = buildTaskPropsByType(taskType, state$, props$);

  const onClickEdit = createEditEvent(taskType, task$, dispatch, props$);

  const parent$ = state$
    .map(taskType === TaskType.ASSET ? getAssetProjectModel : getProjectModel)
    .distinctUntilChanged();

  const fileButtonsEnabled$ = props$
    .map(props => props.permissionsData)
    .distinctUntilChanged()
    .map(permissionsData => getFileButtonsEnabled(permissionsData, taskType, featureSupport));

  return props$
    .combineLatest(
      stateParams$,
      task$,
      breadcrumbPath$,
      parent$,
      fileButtonsEnabled$,
      (props, stateParams, task, breadcrumbPath, parent, fileButtonsEnabled) => ({
        ...props,
        ...stateParams,
        taskType,
        // init: !!task,
        // Needs to come from the angular controller due to the way the current router library view caching works.
        // parentId: get(stateParams, 'taskId'),
        task: task || null,
        parentName: get(parent, 'name', null),
        breadcrumbPath,

        showScheduled: !isTemplate,
        showAssignment: !isTemplate,
        showSubmitted: showSubmitted(task),
        showApproved: showApproved(task),
        showCancelled: showCancelled(task),
        ...featureSupport,

        onGoBack,
        onClickEdit,
        onClickItem,
        ...fileButtonsEnabled,
      }));
};

function getShow(taskType) {
  return taskType === TaskType.TEMPLATE ? {
    showSubmitted: neverShow,
    showApproved: neverShow,
    showCancelled: neverShow,
  } : {
    showSubmitted,
    showApproved,
    showCancelled,
  };
}

function neverShow() {
  return false;
}

function showSubmitted(task) {
  const status = task && task.status;
  return status === 'submitted' || status === 'approved';
}

function showApproved(task) {
  return task && task.status === 'approved';
}

function showCancelled(task) {
  return task && task.status === 'cancelled';
}

function getFileButtonsEnabled(permissionsData, taskType, featureSupport) {
  const {showCameraButton, showVideoButton, showUploadButton, showIOSUpButton, enableFileUpload} = featureSupport;
  const hasPermission = permissionsData.hasPermission;
  const roleData = permissionsData.roleData;
  return {
    isCameraButtonEnabled: status => showCameraButton && checkCanAddFile(hasPermission, roleData, status, taskType),
    isVideoButtonEnabled: status => showVideoButton && checkCanAddFile(hasPermission, roleData, status, taskType),
    isUploadButtonEnabled: status => showUploadButton && checkCanAddFile(hasPermission, roleData, status, taskType),
    isUpIOSButtonEnabled: status => showIOSUpButton && checkCanAddFile(hasPermission, roleData, status, taskType),
    isFileSelectEnabled: status => enableFileUpload && checkCanAddFile(hasPermission, roleData, status, taskType),
  };
}

/**
 * @param {TaskType} taskType
 * @param {Observable} task$
 * @param {Function} dispatch
 * @param {Observable} props$
 */
function createEditEvent(taskType, task$, dispatch, props$) {
  const {
    handler: onClickEdit,
    stream: edit$,
  } = createEventHandler();

  const showEditModal = getEditTaskAction(taskType);

  edit$
    .withLatestFrom(task$)
    .takeUntil(props$.last())
    .subscribe(([e, task]) => {
      e.stopPropagation();
      dispatch(showEditModal({model: task}));
    });

  return onClickEdit;
}

function getEditTaskAction(taskType) {
  switch (taskType) {
    case TaskType.ASSET:
      return updateAssetTaskRequestModal.actions.showModal;

    case TaskType.PROJECT:
      return updateProjectTaskRequestModal.actions.showModal;

    case TaskType.TEMPLATE:
      return updateTemplateTaskRequestModal.actions.showModal;
  }
}

/**
 * @param requirement
 * @return {ReduxRequestActions|ReactReduxModalActions}
 */
function getRequirementUpdateModalActions(requirement) {
  switch (getCollection(requirement)) {

    case FILE_REQUIREMENT_COLLECTION:
      return updateTemplateFileRequirementRequestModal.actions;

    case FORM_REQUIREMENT_COLLECTION:
      return updateTemplateFormRequirementRequestModal.actions;

  }
}

/**
 * @param requirement
 * @return {String}
 */
function getRequirementDetailsState(requirement) {
  switch (getCollection(requirement)) {
    case ASSET_FILE_REQUIREMENT_COLLECTION:
      return assetFileRequirementStates.details;

    case FILE_REQUIREMENT_COLLECTION:
      return projectFileRequirementStates.details;

    case ASSET_FORM_REQUIREMENT_COLLECTION:
      return assetFormRequirementStates.details;

    case FORM_REQUIREMENT_COLLECTION:
      return projectFormRequirementStates.details;

  }
}



// WEBPACK FOOTER //
// ./src/js/tasks/components/TaskViewRequirements/TaskViewRequirementsContainer.js